/**
 * The HTTP shape a **first-party browser endpoint** answers: CORS against a configured
 * allowlist, `no-store`, and a body-free log line.
 *
 * Written for the login SPA's endpoints (`/auth/onboard`, #53; `/auth/create-family`, #97;
 * `/auth/authorize`, #106) and now shared with the control API's `/app` family (#297), which
 * is a different SPA on a different host with a different set of methods.
 *
 * **The SPA is a different origin,** so all of them answer CORS: `login-dev.iamboone.app`
 * posts to `mcp-dev.iamboone.app`, and `my-dev.iamboone.app` calls `/app` on
 * `api-dev.iamboone.app`. An origin is
 * **matched against configuration and only then reflected**, never echoed unvalidated — a
 * write that creates a membership is not something any origin should be able to trigger a
 * browser into making.
 *
 * **It became an allowlist because there is more than one first-party host** (#297): a single
 * `access-control-allow-origin` cannot name two, so serving both from one constant is not
 * available, and `*` is not an answer on a credentialed API. What each family accepts is
 * therefore a field on its {@link CorsPolicy} rather than a global — the login SPA has no
 * business reading an `/app` response, and the panel signs in by *navigating* to the login
 * host rather than fetching it, so neither list needs the other's entry. That is a narrow
 * gain and worth naming honestly: script on a compromised first-party origin could exfiltrate
 * a session credential and call from outside a browser, where CORS does not exist. **The
 * control is the explicit credential** (`app/credential.ts`); this is depth behind it.
 *
 * **`access-control-allow-credentials` is deliberately never sent.** No endpoint here reads a
 * cookie, and advertising credentialed CORS would be an invitation to start.
 *
 * Everything is `no-store`, and the log line is **structural only**: no email, token,
 * session, or family name is ever recorded (an acceptance criterion of #53, #97 and #297),
 * matching `oauthRoutes.log`. Sharing that here rather than copying it per route is what
 * keeps "the endpoint never logs a body" a property of the seam instead of a habit each
 * new route has to remember.
 */

import { SESSION_HEADER } from "./sessionCredential.ts";
import type { StatedWriteChannel } from "./writeChannel.ts";

/**
 * The channel every write behind these endpoints records (issue #313).
 *
 * It sits here rather than in `packages/db`, which is the whole of that issue: the two
 * membership writers said `"web"` themselves, correct only because these endpoints are
 * reached from the login SPA — a reachability argument of exactly the shape #296 deleted
 * from `scoped.ts`. A storage-layer writer that asserts something about its callers is the
 * thing that goes on being satisfied, and wrong, when a second caller appears.
 *
 * **What it does not do is make `web` provable, and that residue is the honest state of
 * this.** These endpoints authenticate a **Stytch session**, and a session is not evidence
 * of a browser: `corsHeaders` sets *response* headers only, so a valid session reaches them
 * from anything, and ADR 0021 decision 11 puts a mobile client on the roadmap that would
 * POST one identically. What moving the constant buys is that the day such a client exists,
 * the answer is **`mobile`, stated at whatever distinguishes it** — a member of
 * `WriteChannel` since ADR 0025 decision 6, so it is a one-line change here, in `/auth`,
 * where the surface question lives, rather than a re-interpretation of `web` discovered
 * inside a shared writer two packages down. It is deliberately *not* closed by trusting a
 * client-supplied hint; nothing on the wire distinguishes these callers today, and
 * inventing a signal that a caller controls would make the field weaker than leaving the
 * limit written down.
 */
export const SPA_CHANNEL: StatedWriteChannel = "web";

/** The login SPA's origin — the host `/auth/*` is called from. */
export const LOGIN_ORIGIN_ENV = "PILOTFISH_LOGIN_ORIGIN";

/**
 * What one route family answers a browser with.
 *
 * `origins` holds **environment-variable names, not values**, and is resolved per request for
 * the reason `jsonResponseEnabled` is: a value read at module load makes changing it a
 * redeploy of the bundle rather than a change to the function's configuration.
 */
export interface CorsPolicy {
  /** The env vars naming the origins this family reflects. An unset one is simply absent. */
  readonly origins: readonly string[];
  /** The `access-control-allow-methods` value, and the `Allow` on a 405. */
  readonly methods: string;
  /** The `access-control-allow-headers` value. */
  readonly headers: string;
}

/** The `/auth/*` endpoints: the login SPA, POSTs only, nothing but a JSON content type. */
export const SPA_CORS: CorsPolicy = {
  origins: [LOGIN_ORIGIN_ENV],
  methods: "POST, OPTIONS",
  headers: "content-type",
};

/**
 * What the login SPA's one **read** answers (`GET /auth/households`).
 *
 * A second policy rather than widening {@link SPA_CORS}, for the reason that file gives for
 * having policies per route family at all: the writes behind `SPA_CORS` are three
 * membership-creating POSTs, and adding `GET` to their allowance would let an origin that
 * only needs to ask a question be preflighted for the ones that change something.
 *
 * **`x-boone-session` is in `headers` because a GET has no body to put it in.** That is
 * `app/credential.ts`'s argument arriving on this seam: the `/auth` POSTs carry `sessionJwt`
 * in JSON, `fetch` forbids a body on GET, and a custom header has the same two properties the
 * body has — a cross-site `<form>` or `<img>` can set neither, and both force a preflight a
 * non-allowlisted origin fails before the real request is sent. The header name is imported
 * from that module rather than spelled again here, so the two seams cannot drift.
 */
export const SPA_READ_CORS: CorsPolicy = {
  origins: [LOGIN_ORIGIN_ENV],
  methods: "GET, OPTIONS",
  headers: `content-type, ${SESSION_HEADER}`,
};

function configuredOrigins(policy: CorsPolicy): readonly string[] {
  return policy.origins
    .map((name) => process.env[name])
    .filter((origin): origin is string => origin !== undefined && origin.length > 0);
}

/**
 * The CORS headers for one request under one policy.
 *
 * **`vary: origin` is sent whether or not the origin matched.** The response body differs by
 * origin, so a cache that did not vary on it could serve an allowed origin's response to a
 * refused one. Nothing in front of this caches today (ADR 0008 makes the MCP distribution
 * `CachingDisabled`), which is exactly why the header has to be right here rather than
 * relied on there.
 */
export function corsHeaders(request: Request, policy: CorsPolicy): Record<string, string> {
  const origin = request.headers.get("origin");
  if (origin === null || !configuredOrigins(policy).includes(origin)) {
    return { vary: "origin" };
  }
  return {
    "access-control-allow-origin": origin,
    "access-control-allow-methods": policy.methods,
    "access-control-allow-headers": policy.headers,
    vary: "origin",
  };
}

/**
 * A JSON response: CORS, `no-store`, nothing cacheable and nothing negotiable.
 *
 * The headers are passed in already computed rather than derived here, so one request is
 * matched against the allowlist once and every response on that path — including the
 * refusals — carries the same answer.
 */
export function json(body: unknown, status: number, cors: Record<string, string>): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "content-type": "application/json", "cache-control": "no-store", ...cors },
  });
}

/** The CORS preflight, which every family answers and no family authenticates. */
export function preflight(cors: Record<string, string>): Response {
  return new Response(null, { status: 204, headers: cors });
}

/**
 * `allow` is passed separately rather than read out of `cors`, because a refused origin gets
 * no `access-control-allow-methods` at all and a 405 still has to say what it would have
 * accepted. It is also not always the policy's blanket list: a family whose routes vary by
 * path answers for *that* path.
 */
export function methodNotAllowed(allow: string, cors: Record<string, string>): Response {
  return new Response(JSON.stringify({ error: "method_not_allowed" }), {
    status: 405,
    headers: {
      "content-type": "application/json",
      "cache-control": "no-store",
      allow,
      ...cors,
    },
  });
}

export interface RouteLogEntry {
  readonly method: string;
  readonly path: string;
  readonly status: number;
  readonly userAgent: string | null;
}

/**
 * One structural log line, labelled by route. The four fields are the whole vocabulary
 * on purpose — there is no parameter here through which a body could be logged.
 */
export function logRoute(label: string, entry: RouteLogEntry): void {
  console.info(JSON.stringify({ [label]: entry }));
}
